/**
 * One plugin in the plugin browser, as a card like Unreal Engine's: its picture (by shelf), name,
 * version and badges, what it does, who wrote it, and the Enabled box on the right. Under it:
 * what it adds (windows to open, commands to run, requests, kinds), the plugins it requires
 * (each marked on or missing), the permissions it asks for (and "Allow and turn on" while they
 * wait for approval), its problem in red if it cannot run, and Remove (installed plugins only,
 * click twice).
 */
import { For, Show, type JSX } from "solid-js";
import type { PluginInfo } from "@protocol/PluginInfo";
import { useApp } from "../../app/context";
import { isReady, pluginCommand } from "../../state/plugins";
import { Icon } from "../../shared/icons";
import type { IconName } from "../../shared/menus";
import { ArmedButton } from "../../shared/ui";

/** Only `http(s)` links are made clickable (a manifest is written by anyone). */
function safeLink(url: string): string | null {
  return /^https?:\/\//i.test(url) ? url : null;
}

/** The picture of a known shelf; other shelves show their first letter. */
const SHELF_ICONS: Record<string, IconName> = { Packs: "presets", Languages: "scan", Tools: "plugins", Views: "windows" };

/** One plugin's card. */
export function PluginCard(props: { plugin: PluginInfo }): JSX.Element {
  const { plugins, wm, access } = useApp();
  /** Whether this page may choose the project's plugins (a space's owner; anyone elsewhere). */
  const mayChoose = () => access.can("owner");
  const p = () => props.plugin;
  /** The name of a required plugin, as the list knows it. */
  const nameOf = (id: string) => plugins.list().find((q) => q.id === id)?.name ?? id;
  /** What the Enabled box's tooltip says. */
  const switchTitle = () =>
    !mayChoose() ? "The space's owners choose its plugins" : p().needs_approval ? "Turning it on in this project allows what it asks for (below)" : p().enabled ? "Turn it off in this project" : "Turn it on in this project";
  return (
    <li class="plugin" data-plugin={p().id} data-ready={isReady(p()) ? "yes" : "no"} classList={{ off: !p().enabled }}>
      <div class="plugin-tile" aria-hidden="true">
        <Show when={SHELF_ICONS[p().category]} fallback={<span>{(p().category || p().name).slice(0, 1).toUpperCase()}</span>}>
          {(icon) => <Icon name={icon()} size={26} />}
        </Show>
      </div>
      <div class="plugin-body">
        <div class="plugin-head">
          <strong class="plugin-title">{p().name}</strong>
          <Show when={p().version}>
            <span class="muted small">v{p().version}</span>
          </Show>
          <Show when={p().builtin}>
            <span class="badge">Built in</span>
          </Show>
          <Show when={p().category}>
            <span class="badge quiet">{p().category}</span>
          </Show>
        </div>
        <Show when={p().description}>
          <p class="small">{p().description}</p>
        </Show>
        <p class="small muted plugin-meta">
          <span class="mono">{p().id}</span>
          <Show when={p().author}> · by {p().author}</Show>
          <Show when={p().files.length}>
            {" "}
            · reads{" "}
            <For each={p().files}>
              {(f, i) => (
                <>
                  <code>{f}</code>
                  {i() < p().files.length - 1 ? " " : ""}
                </>
              )}
            </For>
          </Show>
          <Show when={safeLink(p().homepage)}>
            {(url) => (
              <>
                {" "}
                ·{" "}
                <a href={url()} target="_blank" rel="noopener noreferrer">
                  homepage
                </a>
              </>
            )}
          </Show>
        </p>
        <Show when={p().requires.length}>
          <p class="small plugin-requires" data-plugin-requires={p().id}>
            Requires:{" "}
            <For each={p().requires}>
              {(id) => (
                <span class="req" classList={{ missing: p().missing.includes(id) }} data-required={id} title={p().missing.includes(id) ? "Not on: this plugin waits for it" : "On"}>
                  {p().missing.includes(id) ? "✗" : "✓"} {nameOf(id)}
                </span>
              )}
            </For>
          </p>
        </Show>
        <Show when={p().windows.length || p().commands.length || p().requests.length || p().kinds.nodes.length || p().kinds.links.length}>
          <div class="plugin-adds small" data-plugin-adds={p().id}>
            <For each={p().windows}>
              {(w) => (
                <button type="button" class="mini" data-plugin-open={pluginCommand(p().id, w.name)} disabled={!isReady(p())} title="Open this window" onClick={() => wm.open(pluginCommand(p().id, w.name))}>
                  ▣ {w.label}
                </button>
              )}
            </For>
            <For each={p().commands}>
              {(c) => (
                <button type="button" class="mini" data-plugin-run={pluginCommand(p().id, c.name)} disabled={!isReady(p())} title="Run this command" onClick={() => void plugins.run(p().id, c.name)}>
                  ▶ {c.label}
                </button>
              )}
            </For>
            <Show when={p().requests.length}>
              <span class="muted">
                requests:{" "}
                <For each={p().requests}>
                  {(r, i) => (
                    <>
                      <code>{r}</code>
                      {i() < p().requests.length - 1 ? " " : ""}
                    </>
                  )}
                </For>
              </span>
            </Show>
            <Show when={p().kinds.nodes.length || p().kinds.links.length}>
              <span class="muted" data-plugin-kinds={p().id}>
                kinds: {[...p().kinds.nodes, ...p().kinds.links].map((k) => (k.like ? `${k.label} (${k.like})` : k.label)).join(", ")}
              </span>
            </Show>
          </div>
        </Show>
        <Show when={p().permissions.length}>
          <div class="plugin-perms small" classList={{ waiting: p().needs_approval }} data-plugin-permissions={p().id}>
            <span>{p().needs_approval ? "It asks to:" : "It may:"}</span>
            <ul>
              <For each={p().permissions}>{(line) => <li>{line}</li>}</For>
            </ul>
            <Show when={p().needs_approval}>
              <button type="button" class="btn primary" data-plugin-approve={p().id} disabled={plugins.busy()} onClick={() => void plugins.setEnabled(p().id, true)}>
                Allow and turn on
              </button>
            </Show>
          </div>
        </Show>
        <Show when={p().problem}>
          <p class="small err" data-plugin-problem={p().id}>
            {p().problem}
          </p>
        </Show>
      </div>
      <div class="plugin-side">
        <label class="plugin-switch" title={switchTitle()}>
          <input type="checkbox" data-plugin-toggle={p().id} checked={p().enabled} disabled={plugins.busy() || !mayChoose()} onChange={(e) => void plugins.setEnabled(p().id, e.currentTarget.checked)} />
          Enabled
        </label>
        <Show when={!p().builtin}>
          <ArmedButton class="btn" label="Remove" armedLabel="Click again" onConfirm={() => void plugins.remove(p().id)} id={`plugin-remove-${p().id}`} />
        </Show>
      </div>
    </li>
  );
}
